import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  IconButton,
  Collapse,
  List,
  ListItemButton,
  ListItemText,
  ListItem,
  Divider,
  useTheme,
  Tabs,
  Tab,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import SearchableDropdown from '../../FlowBuilder/Common/SearchableDropdown/SearchableDropdown.tsx';
import Tooltip from '@mui/material/Tooltip';
import { useRecoilValue } from 'recoil';
import { userInformationAtom } from '../../../Services/_states/auth.js';
import OrganisationDropdown from '../../Shared/OrganisationDropdown/index.tsx';

interface ConceptBuilderSidebarProps {
  isMobile: boolean;
  selectedSubject: string;
  selectedTopic: string;
  selectedSubtopic: string;
  subjectOptions: Array<{ label: string; value: string }>;
  topicOptions: Array<{ label: string; value: string }>;
  subtopicOptions: Array<{ label: string; value: string }>;
  availableTopics: string[];
  sortedTopics: string[];
  conversations: any[];
  activeConversationId: string | null;
  expandedTopic: string | null;
  onClose?: () => void;
  onSubjectChange: (subject: string) => void;
  onTopicChange: (topic: string) => void;
  onSubtopicChange: (subtopic: string) => void;
  onTopicClick: (topic: string) => void;
  onExpandTopic: (topic: string | null) => void;
  onNewChatClick: (topic: string) => void;
  onConversationClick: (conversationId: string) => void;
  onTabChange?: (tabValue: number) => void;
  isSuperuser?: boolean;
  availableClasses?: string[];
  superuserSelectedClass?: string;
  onSuperuserClassChange?: (cls: string) => void;
  selectedOrganisation?: any;
  onOrganisationChange?: (org: any) => void;
}

const ConceptBuilderSidebar: React.FC<ConceptBuilderSidebarProps> = ({
  isMobile,
  selectedSubject,
  selectedTopic,
  selectedSubtopic,
  subjectOptions,
  topicOptions,
  subtopicOptions,
  availableTopics,
  sortedTopics,
  conversations,
  activeConversationId,
  expandedTopic,
  onClose,
  onSubjectChange,
  onTopicChange,
  onSubtopicChange,
  onTopicClick,
  onExpandTopic,
  onNewChatClick,
  onConversationClick,
  onTabChange,
  isSuperuser = false,
  availableClasses = [],
  superuserSelectedClass = '',
  onSuperuserClassChange,
  selectedOrganisation,
  onOrganisationChange,
}) => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const loggedInUser = useRecoilValue(userInformationAtom);


  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    if (onTabChange) {
      onTabChange(newValue);
    }
    //    if (newValue === 1) {
    //     onSubjectChange('');
    //   onTopicChange('');
    //   onSubtopicChange('');
    // }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Sidebar Header */}
      <Box
        sx={{
          p: 1.5,
          bgcolor: 'primary.main',
          color: '#fff',
          borderRadius: isMobile ? 0 : '12px 12px 0 0',
          flexShrink: 0,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box>
          <Typography fontWeight={700}>Filters</Typography>
          <Typography fontSize={12} opacity={0.9}>
            {selectedSubject
              ? `${selectedSubject}${selectedTopic ? ` → ${selectedTopic}` : ''}`
              : 'Select context'}
          </Typography>
        </Box>
        {isMobile && onClose && (
          <IconButton onClick={onClose} sx={{ color: '#fff', ml: 'auto' }}>
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      {/* Sidebar Content */}
      <Box sx={{ p: 1.5, flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
        {/* Dropdowns */}
        <Grid container spacing={1.5}>
          {loggedInUser?.is_superuser && (
            <Grid item xs={12}>
              <OrganisationDropdown
                label={"Select Organisation"}
                value={selectedOrganisation}           // ← ADD
                onChange={(val) => onOrganisationChange?.(val)}  // ← ADD
              />
            </Grid>
          )}
          {isSuperuser && (
            <Grid item xs={12}>
              <SearchableDropdown
                options={availableClasses.map((c) => ({
                  label: `Class ${c}`,
                  value: c,
                }))}
                label="Class *"
                size="small"
                value={
                  superuserSelectedClass
                    ? { label: `Class ${superuserSelectedClass}`, value: superuserSelectedClass }
                    : null
                }
                onChange={(selected) => onSuperuserClassChange?.(selected?.value || '')}
                labelKey="label"
                valueKey="value"
                disabled={!selectedOrganisation}
              />
            </Grid>
          )}
          {/* Subject */}
          <Grid item xs={12}>
            <SearchableDropdown
              options={subjectOptions}
              label="Subject *"
              size="small"
              value={
                selectedSubject ? { label: selectedSubject, value: selectedSubject } : null
              }
              onChange={(selected) => {
                const subject = selected?.value || '';
                onSubjectChange(subject);
              }}
              labelKey="label"
              valueKey="value"
              disabled={isSuperuser && !superuserSelectedClass}
            />
          </Grid>

          {/* Topic */}
          <Grid item xs={12}>
            <SearchableDropdown
              options={topicOptions}
              label="Topic"
              size="small"
              value={selectedTopic ? { label: selectedTopic, value: selectedTopic } : null}
              onChange={(selected) => {
                const topic = selected?.value || '';
                onTopicChange(topic);
              }}
              labelKey="label"
              valueKey="value"
              disabled={!selectedSubject}
            />
          </Grid>

          {/* Subtopic */}
          <Grid item xs={12}>
            <SearchableDropdown
              options={subtopicOptions}
              label="Subtopic"
              size="small"
              value={
                selectedSubtopic ? { label: selectedSubtopic, value: selectedSubtopic } : null
              }
              onChange={(selected) => {
                onSubtopicChange(selected?.value || '');
              }}
              labelKey="label"
              valueKey="value"
              disabled={!selectedTopic}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 1 }} />

        {/* Tabs for Topic-wise and All Chats */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '13px',
                minHeight: '36px',
                py: 0.5,
              },
            }}
          >
            <Tab label="All Chats" />
            <Tab label="Topic Wise Chats" />
          </Tabs>
        </Box>

        {/* Tab Panels */}
        <Box
          sx={{
            overflowY: 'auto',
            maxHeight: '63vh',
            pr: 1,
            mt: 1,
          }}
        >
          {/* Topic-wise Chat History */}
          {tabValue === 1 && (
            <>
              <List dense component="nav">
                {availableTopics.length === 0 && (
                  <Typography
                    variant="body2"
                    align="center"
                    sx={{ mt: 2, color: theme.palette.text.secondary }}
                  >
                    No topics found. Please select a subject.
                  </Typography>
                )}
                {sortedTopics.map((topic) => {
                  const isExpanded = expandedTopic === topic;

                  // Filter conversations for this specific topic
                  const topicConversations = conversations.filter(
                    (c) =>
                      c.parameters?.subject === selectedSubject && c.parameters?.topic === topic
                  );

                  return (
                    <Box key={topic}>
                      {/* Topic Row */}
                      <ListItem
                        disableGutters
                        sx={{
                          px: 1,
                          py: 0.25,
                          borderRadius: 1,
                          mb: 0.25,
                          cursor: 'pointer',
                          backgroundColor:
                            selectedTopic === topic ? theme.palette.primary.light : '#efefef',
                          color:
                            selectedTopic === topic
                              ? theme.palette.primary.contrastText
                              : 'inherit',
                          '&:hover': {
                            backgroundColor:
                              selectedTopic === topic
                                ? theme.palette.primary.main
                                : '#e0e0e0',
                          },
                        }}
                        onClick={() => onTopicClick(topic)}
                        secondaryAction={
                          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                            <Tooltip title="Start new chat for this topic">
                              <Box
                                component="span"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onSubtopicChange('');
                                  onNewChatClick(topic);
                                }}
                                sx={{
                                  cursor: 'pointer',
                                  fontSize: 18,
                                  color: selectedTopic === topic ? 'black' : 'primary.main',
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.08)',
                                  },
                                }}
                              >
                                +
                              </Box>
                            </Tooltip>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                onExpandTopic(isExpanded ? null : topic);
                              }}
                            >
                              {isExpanded ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                          </Box>
                        }
                      >
                        <ListItemText
                          primary={topic}
                          primaryTypographyProps={{
                            fontSize: 12,
                          }}
                        />
                      </ListItem>

                      {/* Chats under Topic */}
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <List dense sx={{ pl: 2 }}>
                          {topicConversations.length === 0 ? (
                            <Typography
                              variant="body2"
                              sx={{ color: 'text.secondary', pl: 1, py: 1 }}
                            >
                              No chats yet
                            </Typography>
                          ) : (
                            topicConversations.map((conv) => (
                              <ListItem
                                key={conv.conversation_id}
                                sx={{
                                  cursor: 'pointer',
                                  borderRadius: 1,
                                  mb: 0.5,
                                  backgroundColor:
                                    conv.conversation_id === activeConversationId
                                      ? theme.palette.action.selected
                                      : theme.palette.grey[100],
                                  '&:hover': {
                                    backgroundColor: theme.palette.grey[200],
                                  },
                                }}
                                onClick={() => onConversationClick(conv.conversation_id)}
                              >
                                <Box display="flex" alignItems="center" width="100%">
                                  {conv.conversation_id === activeConversationId && (
                                    <Box
                                      sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        backgroundColor: 'green',
                                        mr: 1,
                                        flexShrink: 0,
                                      }}
                                    />
                                  )}

                                  <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    width="100%"
                                  >
                                    <ListItemText
                                      primary={conv.last_query || conv.query || 'No query'}
                                      primaryTypographyProps={{
                                        fontSize: 12,
                                        noWrap: true,
                                        sx: {
                                          textOverflow: 'ellipsis',
                                          overflow: 'hidden',
                                          whiteSpace: 'nowrap',
                                          maxWidth: '80%',
                                        },
                                      }}
                                    />

                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ ml: 1, flexShrink: 0 }}
                                    >
                                      {formatDate(conv.created_at)}
                                    </Typography>
                                  </Box>
                                </Box>
                              </ListItem>
                            ))
                          )}
                        </List>
                      </Collapse>
                    </Box>
                  );
                })}
              </List>
            </>
          )}

          {/* All Chats History */}
          {tabValue === 0 && (
            <>
              <List dense component="nav">
                {conversations.length === 0 ? (
                  <Typography
                    variant="body2"
                    align="center"
                    sx={{ mt: 1, color: theme.palette.text.secondary }}
                  >
                    No conversations yet
                  </Typography>
                ) : (
                  conversations.map((conv) => (
                    <ListItem
                      key={conv.conversation_id}
                      sx={{
                        cursor: 'pointer',
                        borderRadius: 1,
                        mb: 0.5,
                        backgroundColor:
                          conv.conversation_id === activeConversationId
                            ? theme.palette.action.selected
                            : theme.palette.grey[100],
                        '&:hover': {
                          backgroundColor: theme.palette.grey[200],
                        },
                      }}
                      onClick={() => onConversationClick(conv.conversation_id)}
                    >
                      <Box display="flex" flexDirection="column" width="100%">
                        <Box display="flex" alignItems="center" width="100%">
                          {conv.conversation_id === activeConversationId && (
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                backgroundColor: 'green',
                                mr: 1,
                                flexShrink: 0,
                              }}
                            />
                          )}

                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            width="100%"
                          >
                            {/* <ListItemText
                            primary={conv.last_query || conv.query || 'No query'}
                            sx={{
                              overflow: 'hidden',
                            }}
                            primaryTypographyProps={{
                              fontSize: 13,
                              noWrap: true,
                              sx: {
                                textOverflow: 'ellipsis',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                              },
                            }}
                            /> */}
                            <ListItemText
                              primary={conv.last_query || conv.query || 'No query'}
                              primaryTypographyProps={{
                                fontSize: 12,
                                noWrap: true,
                                sx: {
                                  textOverflow: 'ellipsis',
                                  overflow: 'hidden',
                                  whiteSpace: 'nowrap',
                                  maxWidth: '80%',
                                },
                              }}
                            />

                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ ml: 1, flexShrink: 0 }}
                            >
                              {formatDate(conv.created_at)}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </ListItem>
                  ))
                )}
              </List>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ConceptBuilderSidebar;